import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosRequestHeaders } from "axios";
import { RecreateSuccesulResponse, TokenDataSuccessfulResponse } from "models/responses/AuthSuccesfullResponse";

import { SessionStorage } from "./CookieStorage"
import { STORAGEKEYS } from 'enviroment/Keys'
import { Environment } from 'enviroment/AppSettings'
import apphistory from './AppHistory'
import { AppLinks } from 'enviroment/AppLinks'


interface AdaptAxiosRequestConfig extends AxiosRequestConfig {
  headers: AxiosRequestHeaders
}

export interface StartLinkVisitParams {
  isStartLink?: boolean
  source?: string | null
}


export class HttpClient {
  private readonly _instance: AxiosInstance;
  private readonly _storage: SessionStorage;
  private readonly _errorStatus = [400, 404, 500];
  private readonly _authErrors = [401, 403];
  private readonly _ignoreErrorMessages = ["timeout of 2000ms exceeded"];


  public constructor(baseURL: string) {
    this._instance = axios.create({
      baseURL,
    });
    
    this._storage = new SessionStorage();
    this._initializeResponseInterceptor();
  }

  private _initializeResponseInterceptor = () => {
    this._instance.interceptors.response.use(
      this._handleResponse,
      this._handleError
    );
  };

  private _handleResponse = (data : AxiosResponse) => data;


  protected _handleError = (error: any) => {
      if(this._authErrors.filter(p=>p === error.status).length > 0){
         apphistory.push(AppLinks.sessionEnd)
      }
      else if(this._errorStatus.filter(p=>p === error.status).length > 0 || this._ignoreErrorMessages.filter(s => s === error.message) == null) {
         apphistory.push(AppLinks.error)
      }

      throw error;
  };

  private _handleRequest = (config: AdaptAxiosRequestConfig) => {
    const ACCESS_TOKEN = this._storage.get(STORAGEKEYS.ACCESS_TOKEN)
    config.headers["Authorization"] = `bearer ${ACCESS_TOKEN}`
  
    return config;
  };

  public get<T>(url: string, isAuth: boolean = false, timeout: number = 10000): Promise<AxiosResponse<T>> {
    if (isAuth) {
      this._instance.interceptors.request.use(this._handleRequest);
    }
    return this._instance.get(url, {
      headers: { "content-type": "application/json" },
      timeout: timeout,
      withCredentials : true,
    });
  }

  public post<T, Body>(url: string, body: Body, isAuth: boolean = false): Promise<AxiosResponse<T>> {
    if (isAuth) {
      this._instance.interceptors.request.use(this._handleRequest);
    }
    
    return this._instance.post(url, body, {
      headers: { "content-type": "application/json" },
      withCredentials : true,
    });
  }

  public put<Body>(url: string, body: Body, isAuth: boolean = false): Promise<AxiosResponse> {
    if (isAuth) {
      this._instance.interceptors.request.use(this._handleRequest);
    }
    return this._instance.put(url, body, {
      headers: { "content-type": "application/json" },
      withCredentials : true,
    });
  }

  public download (url: string, isAuth: boolean = false) : Promise<AxiosResponse<Blob>> {
    if(isAuth){
      this._instance.interceptors.request.use(this._handleRequest);
    }

    return this._instance.get(url, {
      headers: { "content-type": "application/json" },
      withCredentials : true,
      responseType: 'blob'
    });
  }

  public authRequest(orderId : string, startLinkVisit?: StartLinkVisitParams): Promise<AxiosResponse<TokenDataSuccessfulResponse>> {
    const formData = new URLSearchParams();

    formData.append("client_id", Environment.clientId);
    formData.append("client_secret", Environment.clientSecret);
    formData.append("scope", Environment.scope);
    formData.append("grant_type", Environment.grantType);
    formData.append("orderId", orderId);
    this.appendStartLinkVisitParams(formData, startLinkVisit)

    return this._instance.post("/connect/token", formData.toString(), {
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
      },
    });
  }

  public authRequestByUuid(uuid: string) : Promise<AxiosResponse<RecreateSuccesulResponse>>{
    const formData = new URLSearchParams();

    formData.append("client_id", Environment.clientId);
    formData.append("client_secret", Environment.clientSecret);
    formData.append("scope", Environment.scope);
    formData.append("grant_type", Environment.grantType);
    formData.append("bm_uuid", uuid);

    return this._instance.post("/connect/token/bm", formData.toString(), {
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
      },
    });
  }

  private appendStartLinkVisitParams(formData: URLSearchParams, startLinkVisit?: StartLinkVisitParams) {
    if (!startLinkVisit?.isStartLink) {
      return
    }

    formData.append("isStartLink", "true")
    const source = startLinkVisit.source?.trim()
    if (source) {
      formData.append("source", source)
    }
  }
}